import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

interface GetInvoicesPropsType {
  countInvoices: number;
  lastWeekStartDate: number | null;
}

export const getInvoices = createAsyncThunk(
  'auth/getInvoices',
  async ({ countInvoices, lastWeekStartDate }: GetInvoicesPropsType) => {
    const { data } = await axios.get(`/user/accounting/invoices`, {
      params: {
        count: countInvoices,
        lastWeekStartDate
      }
    });

    return data.data.invoices as Invoice[];
  }
);

interface GetTransactionsPropsType {
  countTransactions: number;
  numberOfPage: number;
}

export const getTransactions = createAsyncThunk(
  'auth/getTransactions',
  async ({ countTransactions, numberOfPage }: GetTransactionsPropsType) => {
    const { data } = await axios.get(`/user/accounting/transactions`, {
      params: {
        countPerPage: countTransactions,
        numberOfPage
      }
    });

    const transactions = data.data.transactions as Transaction[];

    // izbaci transakcije kod kojih su svi poeni 0
    return transactions.filter(
      (transaction) =>
        transaction.rewardPoints !== 0 ||
        transaction.shoppingPoints !== 0 ||
        transaction.loyaltyPoints !== 0 ||
        transaction.myLifePoints !== 0 ||
        transaction.ulxPoints !== 0
    );
  }
);

interface GetTransfersPropsType {
  countTransfers: number;
  numberOfPage: number;
}

export const getTransfers = createAsyncThunk(
  'auth/getTransfers',
  async ({ countTransfers, numberOfPage }: GetTransfersPropsType) => {
    const { data } = await axios.get(`/user/accounting/transfers`, {
      params: {
        countPerPage: countTransfers,
        numberOfPage
      }
    });

    return data.data.transfers as Transfers[];
  }
);

interface GetWithdrawalsPropsType {
  countWithdrawals: number;
  numberOfPage: number;
}

export const getWithdrawals = createAsyncThunk(
  'auth/getWithdrawals',
  async ({ countWithdrawals, numberOfPage }: GetWithdrawalsPropsType) => {
    const { data } = await axios.get(`/user/accounting/withdrawals`, {
      params: {
        countPerPage: countWithdrawals,
        numberOfPage
      }
    });

    return data.data.withdrawals as Withdrawal[];
  }
);

interface GetConversionsPropsType {
  countConversions: number;
  numberOfPage: number;
}

export const getConversions = createAsyncThunk(
  'auth/getConversions',
  async ({ countConversions, numberOfPage }: GetConversionsPropsType) => {
    const { data } = await axios.get(`/user/accounting/conversions`, {
      params: {
        countPerPage: countConversions,
        numberOfPage
      }
    });

    return data.data.conversions as Conversions[];
  }
);

type Invoice = {
  id: number;
  weekStartDate: string;
  weekEndDate: string;
  amount: number;
  isPaid: boolean;
};

type Transaction = {
  id: string;
  dateCreated: string; // Assuming you want to keep this as a string
  rewardPoints: number;
  shoppingPoints: number;
  ulxPoints: number;
  loyaltyPoints: number;
  myLifePoints: number;
  groupType: number;
  status: number;
};

type Withdrawal = {
  id: number;
  dateCreated: string; // Assuming you want to keep this as a string
  datePaid: string; // Assuming you want to keep this as a string
  amount: number;
  feeAmount: number;
  paidAmount: number;
  wallet: string;
  networkType: number;
  status: number;
};

type Conversions = {
  conversionId: string;
  dateCreated: string;
  amount: number;
  type: string;
  status: string;
};

type Transfers = {
  id: number;
  dateCreated: string;
  datePaid: string;
  price: number;
  receivedAmount: number;
  type: string;
  paymentAddress: string;
  networkCurrencyType: string;
  status: number;
  isPartnerNotified: boolean;
};

export interface accountingSliceType {
  loading: boolean;
  invoices: Invoice[];
  transactions: Transaction[];
  withdrawals: Withdrawal[];
  conversions: Conversions[];
  transfers: Transfers[];
}

const initialState = {
  loading: false,
  invoices: [],
  transactions: [],
  withdrawals: [],
  conversions: [],
  transfers: []
} as accountingSliceType;

const accountingSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getInvoices.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getInvoices.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.invoices = payload;
    });
    builder.addCase(getInvoices.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getTransactions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTransactions.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.transactions = payload;
    });
    builder.addCase(getTransactions.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getWithdrawals.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getWithdrawals.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.withdrawals = payload;
    });
    builder.addCase(getWithdrawals.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getConversions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getConversions.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.conversions = payload;
    });
    builder.addCase(getConversions.rejected, (state) => {
      state.loading = false;
    });

    //transfers
    builder.addCase(getTransfers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTransfers.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.transfers = payload;
    });
    builder.addCase(getTransfers.rejected, (state) => {
      state.loading = false;
    });
  }
});

export default accountingSlice.reducer;
